import { ChangeDetectionStrategy, Component, OnDestroy, inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Dialog } from '../../interfaces/create-dialog.interface';
import { AutoUnsubscribe } from '../../decorators/auto-unsubscribe.decorator';

import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

export interface InputDialogData extends Dialog {
  inputLabel?: string;
  inputType?: 'text' | 'password' | 'number' | 'email';
  inputValue?: string;
  inputPlaceholder?: string;
  inputRequired?: boolean;
  inputPattern?: string;
  inputMinLength?: number;
  inputMaxLength?: number;
}

@AutoUnsubscribe()
@Component({
  standalone: true,
  selector: 'app-input-dialog',
  templateUrl: './input-dialog.component.html',
  styleUrls: ['./input-dialog.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputDialogComponent implements OnDestroy {
  data = inject<InputDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject<MatDialogRef<InputDialogComponent>>(MatDialogRef);
  private readonly fb = inject(FormBuilder);

  inputForm: FormGroup;
  showPassword = false;

  constructor() {
    const data = this.data;

    this.inputForm = this.fb.group({
      inputValue: ['', this.getValidators()],
    });

    // Set initial value if provided
    if (data.inputValue) {
      this.inputForm.patchValue({ inputValue: data.inputValue });
    }
  }

  // eslint-disable-next-line
  ngOnDestroy() {
    // This is intentional
  }

  private getValidators() {
    const validators = [];

    if (this.data.inputRequired) {
      validators.push(Validators.required);
    }

    if (this.data.inputPattern) {
      validators.push(Validators.pattern(this.data.inputPattern));
    }

    if (this.data.inputMinLength) {
      validators.push(Validators.minLength(this.data.inputMinLength));
    }

    if (this.data.inputMaxLength) {
      validators.push(Validators.maxLength(this.data.inputMaxLength));
    }

    if (this.data.inputType === 'email') {
      validators.push(Validators.email);
    }

    return validators;
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  onSubmit() {
    if (this.inputForm.valid) {
      const result = {
        ...this.data,
        inputValue: this.inputForm.get('inputValue')?.value,
      };
      this.dialogRef.close(result);
    }
  }
}
